import { useEffect, useState } from 'react'

import {
  ApiError, loggbok, loggbokPath,
  type LoggbokDag, type LoggbokForslag,
} from '../api'
import { ErrorBox } from '../components/bits'
import { Sporet } from '../components/Sporet'
import { Svarrad } from '../components/Svarrad'
import { Link, navigate } from '../router'
import { useApi } from '../useApi'

/* Besøket, hele skjermen.
 *
 * HVORFOR DETTE ER EN ADRESSE OG IKKE EN KNAPP SOM FOLDER UT ET KORT.
 *
 * `Kart.tsx` skrev argumentet først og det gjelder ordrett her: et norsk anlegg
 * er tre hundre meter på skrå, og en ramme rundt et skråstilt anlegg er stort
 * sett sjø. I et kort på 460 piksler kom Ljøsøys sju ringer ut på størrelse med
 * en fingernegl, med fjorden som gjorde ingenting på hver side -- et bilde av
 * anlegget som bare kan skjeles til, ikke jobbes på. Alt kartet er TIL for --
 * ligger de ved merd 4, sto de stille, når var det bråk -- krever at ringen er
 * på størrelse med en tommel.
 *
 * En adresse og ikke en modus på grunn av hvor den brukes: den står på veggen i
 * fôrflåta, den sendes i en melding som «se på ANDREAS i går», og den lastes på
 * nytt på gangbanen når dekningen kommer tilbake. Ingen av de tre overlever en
 * tilstand som bare finnes i minnet til én fane.
 *
 * SVARET ER MED HIT. Å sende noen bort fra kortet for å se, og så tilbake for å
 * svare, er to reiser for én avgjørelse -- og den andre er den man glemmer.
 * Man ser og svarer på samme skjerm, og da er turen ferdig.
 */
export function SiteLoggbokSpor({ id }: { id: string }) {
  const p = new URLSearchParams(window.location.search)
  const on = p.get('on') || ''
  const mmsiTekst = p.get('mmsi')
  const mmsi = mmsiTekst && /^\d+$/.test(mmsiTekst) ? Number(mmsiTekst) : null

  const [lagrer, setLagrer] = useState(false)
  const [feil, setFeil] = useState<ApiError | Error | null>(null)

  /* KARTSIDA TAR HELE VINDUET. Samme grep som `Kart.tsx`: skinna, topplinja og
     fanelinja forsvinner, og sjøen går kant til kant. Klassen tas av igjen ved
     avmontering, ellers blir resten av appen uten navigasjon. */
  useEffect(() => {
    document.body.classList.add('kartsiden')
    return () => document.body.classList.remove('kartsiden') }, [])

  const q = useApi<LoggbokDag>(loggbokPath(id, on || undefined))
  const dag = q.data
  const tilbake = `/sites/${encodeURIComponent(id)}/loggbok${
    on ? `?on=${encodeURIComponent(on)}` : ''}`

  /* Kortet dette besøket hører til, hvis det fortsatt er ubesvart. Funnet på
     MMSI framfor på forslags-id: en id i en URL som ble delt i går peker på et
     forslag som kan være besvart, mens «ANDREAS den 28.» fortsatt er den samme
     båten samme dag. */
  const kort: LoggbokForslag | null = dag?.proposals.find(
    (f) => f.state === 'open' && f.source === 'hour_vessels'
      && f.evidence?.mmsi === mmsi) ?? null

  const svar = async (key: string) => {
    if (!kort || !on) return
    setLagrer(true)
    setFeil(null)
    try {
      await loggbok.skriv(id, {
        on_date: on, kind: kort.kind, answer: key,
        occurred_at: kort.occurred_at, proposal_ids: [kort.proposal_id],
        detail: mmsi !== null ? { mmsi } : {},
      })
      /* Svart, altså ferdig her. Tilbake til køen framfor å bli stående på et
         kart over noe som nettopp ble besvart -- runden er en rekke, og det
         neste kortet er det man skal se. */
      navigate(tilbake)
    } catch (e) {
      setFeil(e as ApiError)
      setLagrer(false)
    }
  }

  if (!on) return <Redirigert to={tilbake} />

  return (
    <div className="kartsiden-flate">
      <header className="kartsiden-topp">
        <Link className="btn ghost sm" to={tilbake}>Tilbake</Link>
        <div>
          <strong>{kort ? kort.headline : 'Besøket'}</strong>
        </div>
      </header>

      <div className="spor-side">
        <Sporet siteId={id} on={on} mmsi={mmsi} />

        {/* Svarene på kartet. Samme rad, samme ord og samme høyde som i køen:
            det er det samme spørsmålet, stilt der beviset er. */}
        {kort && (
          <div className="spor-svar">
            <Svarrad svar={kort.answers} laast={lagrer} onSvar={svar} />
          </div>
        )}
        {feil && <div className="spor-svar"><ErrorBox error={feil} /></div>}
        {/* Et besvart kort er ikke en feil å beklage. Det er den vanlige
            tilstanden for et besøk man åpner igjen en uke senere, og siden sier
            hva den er framfor å stå med en tom rad. */}
        {!kort && dag && (
          <p className="fjernt spor-svar">
            dette besøket er allerede ført — sporet står her som dokumentasjon
          </p>
        )}
      </div>
    </div>
  )
}

function Redirigert({ to }: { to: string }) {
  useEffect(() => { navigate(to, true) }, [to])
  return null
}
